// AuthLayer.tsx
import { useContext, useEffect } from "react";
import Loader from "../components/common/Loader";
import jwt_decode from "jwt-decode";
import { getCookie, setCookie } from "../utils/Common";
import { RootContext } from "./RootContext";
import {
  customerData,
  fetchPartnerMap,
  orgBucketResponse,
} from "../services/network";
import short from "short-uuid";
import { useClientConfig } from "../hooks/useClientConfig";
import { Roles } from "../utils/enums";

const testMerchantsId = "517bd3a1-5d5f-4356-bace-5fd28743cf45";
const CALLBACK_URI = "/callback";

export const AuthLayer = (props: any) => {
  const {
    setDecodedTokenData,
    setUserPermissions,
    setDynamicLoader,
    dynamicConfig,
    orgData,
    setDscrPartner,
    setDSCROrgPartnerMapping,
    userPermissions,
    setShowTermSheet,
    setLoanTypes,
    setOrgData,
    setUserRole,
    setDynamicConfig,
    isAppReady,
    setIsAppReady,
  } = useContext(RootContext) ?? {};

  useClientConfig(setDynamicConfig);

  const isAuthenticated = Boolean(getCookie("sizer_access_token"));

  // ✅ On refresh — cookies already exist, mark app ready immediately
  useEffect(() => {
    if (isAuthenticated && getCookie("orgId")) {
      setIsAppReady(true);
    }
  }, [isAuthenticated]);

  // ✅ Restore data from cookies on refresh
  useEffect(() => {
    const data = getCookie("loanTypes");
    const tempOrgData =
      getCookie("originatorData") ||
      localStorage?.getItem("originatorData") ||
      "{}";
    if (data && data !== "undefined") {
      setLoanTypes(JSON.parse(data ?? "{}"));
    }
    if (!["undefined", "null"].includes(tempOrgData))
      setOrgData(JSON.parse(tempOrgData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Restore permissions and role from cookies on refresh
  useEffect(() => {
    if (isAuthenticated) {
      getCreditLineData();
    }
    if (isAuthenticated && userPermissions?.length) {
      const showTermSheetData = userPermissions.includes("generate_term_sheet");
      setShowTermSheet(showTermSheetData);
      const decodedToken: any = jwt_decode(
        getCookie("sizer_access_token") || ""
      );
      setDecodedTokenData(decodedToken);
      if (userPermissions.includes(Roles.loanOfficer)) {
        setUserRole("loanOfficer");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userPermissions, orgData]);

  async function getCreditLineData() {
    fetchPartnerMap()
      .then((res) => {
        setDscrPartner(res?.data);
        const partnersData: any = {};
        res?.data?.map((creditLineData: any) => {
          partnersData[creditLineData?.id] = creditLineData?.programName;
        });
        setDSCROrgPartnerMapping(partnersData);
      })
      .catch(console.error);
  }

  // ✅ Not authenticated + no code → redirect to Keycloak
  // /callback handles token exchange — AuthLayer no longer does this
  if (!isAuthenticated && !getCookie("code")) {
    const translator = short();
    const stateUUID = translator.uuid();
    const nonceUUID = translator.uuid();
    window.location.replace(
      `${dynamicConfig?.ViteAppRealms}/auth?client_id=${dynamicConfig?.ViteAppClientId}&redirect_uri=${dynamicConfig?.ViteAppHostRedirectUri}${CALLBACK_URI}&state=${stateUUID}&nonce=${nonceUUID}&response_mode=query&response_type=code&scope=openid`
    );
    return <Loader isOpen={true} />;
  }

  // ✅ Fresh login — /callback is handling token exchange, don't block here
  // isAppReady will be set by CallbackPage after token + orgId are ready
  if (isAuthenticated && !isAppReady) {
    return <Loader isOpen={true} />;
  }

  return props.children;
};